// src/pages/LoginAnalytics.jsx
import React, { useEffect, useRef, useState, useContext } from "react";
import { Card, CardBody, Button } from "reactstrap";
import { Loader } from "@googlemaps/js-api-loader";
import { request } from "../../utils/request";
import { ColorModeContext } from "../../index"; // theme context
import "./dashboard.scss";

const DEFAULT_CENTER = { lat: 20, lng: 0 };
const DEFAULT_ZOOM = 2;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Module-level singleton loader so the Maps JS SDK is only ever requested
// once, even if this component mounts/unmounts multiple times.
//
// region: "IN" + language: "en" is what makes this render Jammu & Kashmir
// as a solid, undisputed part of India (matching what you'd see on
// maps.google.co.in) instead of the dashed "disputed territory" outline
// Google (and OpenStreetMap/Leaflet) shows to viewers outside India.
const mapsLoader = new Loader({
  apiKey: GOOGLE_MAPS_API_KEY,
  version: "weekly",
  region: "IN",
  language: "en",
});

function normalizeResponse(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (resp.data && Array.isArray(resp.data)) return resp.data;
  if (resp.sessions && Array.isArray(resp.sessions)) return resp.sessions;
  if (resp.result && Array.isArray(resp.result)) return resp.result;
  if (resp.payload && Array.isArray(resp.payload)) return resp.payload;
  if (Array.isArray(resp.docs)) return resp.docs;
  if (Array.isArray(resp.items)) return resp.items;
  for (const k of Object.keys(resp)) {
    if (Array.isArray(resp[k])) return resp[k];
  }
  return [];
}

function parseLatLngFromGps(gps) {
  if (!gps) return null;

  if (typeof gps === "object" && !Array.isArray(gps)) {
    const rawLat = gps.latitude ?? gps.lat ?? gps.latitud ?? null;
    const rawLng =
      gps.longitude ??
      gps.lng ??
      gps.lon ??
      gps.long ??
      gps.longitude_degrees ??
      null;
    if (rawLat != null && rawLng != null) {
      const clean = (v) =>
        typeof v === "string" ? v.trim().replace(",", "") : v;
      const lat = parseFloat(clean(rawLat));
      const lng = parseFloat(clean(rawLng));
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
          return [lng, lat];
        }
        return [lat, lng];
      }
    }
  }

  if (Array.isArray(gps) && gps.length >= 2) {
    const lat = parseFloat(gps[0]);
    const lng = parseFloat(gps[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
        return [lng, lat];
      }
      return [lat, lng];
    }
  }

  if (typeof gps === "string") {
    const parts = gps.split(",").map((p) => p.trim());
    if (parts.length >= 2) {
      const a = parseFloat(parts[0].replace(/[^0-9.\-+eE]/g, ""));
      const b = parseFloat(parts[1].replace(/[^0-9.\-+eE]/g, ""));
      if (Number.isFinite(a) && Number.isFinite(b)) {
        if (Math.abs(a) > 90 && Math.abs(b) <= 90) {
          return [b, a];
        }
        return [a, b];
      }
    }
  }

  return null;
}

function isValidCoord(coord) {
  if (!coord || coord.length !== 2) return false;
  const [lat, lng] = coord;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  if (Math.abs(lat) < 1e-6 && Math.abs(lng) < 1e-6) return false;
  return true;
}

function haversineKm(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const sLat1 = Math.sin(dLat / 2) ** 2;
  const sLon = Math.sin(dLon / 2) ** 2;
  const A = sLat1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sLon;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(A)));
}

function filterOutliers(items) {
  if (!items || items.length <= 2) return items;

  const coords = items.map((it) => it.coord);
  const len = coords.length;

  const centroid = coords.reduce(
    (acc, [lat, lng]) => {
      acc[0] += lat;
      acc[1] += lng;
      return acc;
    },
    [0, 0]
  );
  centroid[0] /= len;
  centroid[1] /= len;

  const distances = coords.map((c) => haversineKm(c, centroid));
  const sorted = [...distances].sort((a, b) => a - b);
  const medianDist = sorted[Math.floor(sorted.length / 2)] || 0;

  const threshold = Math.max(2000, medianDist * 8);

  const filtered = items.filter((it, i) => distances[i] <= threshold);

  if (filtered.length >= Math.max(1, Math.ceil(len / 2))) {
    return filtered;
  }

  return items;
}

const LoginAnalytics = () => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  const textColor = isDark ? "#f5f5f5" : "#000";
  const cardBg = isDark ? "#252b3b" : "#fff";

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]); // plain array replaces Leaflet's layerGroup
  const infoWindowRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState([]);
  const [mapReady, setMapReady] = useState(false);

  // Initialize map once
  useEffect(() => {
    let cancelled = false;

    mapsLoader
      .load()
      .then((google) => {
        if (cancelled || mapRef.current || !containerRef.current) return;

        const map = new google.maps.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          disableDefaultUI: true, // we render our own +/-/reset buttons
          zoomControl: false,
          gestureHandling: "greedy",
        });

        infoWindowRef.current = new google.maps.InfoWindow();
        mapRef.current = map;
        setMapReady(true);
      })
      .catch((err) => {
        console.error("Failed to load Google Maps:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch sessions and extract GPS coords (unchanged logic)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const raw = await request("sessions", { method: "GET" });

        const arr = normalizeResponse(raw);
        let pts = arr
          .map((s) => {
            const gps = s.gps ?? s.location ?? s.geo ?? null;
            const coords = parseLatLngFromGps(gps);
            if (!coords) return null;
            if (!isValidCoord(coords)) return null;
            return {
              coord: coords,
              meta: {
                email: s.email,
                ip: s.ip,
                loginTime: s.loginTime ?? s.createdAt ?? s.timestamp,
              },
            };
          })
          .filter(Boolean);

        const seen = new Set();
        pts = pts.filter((p) => {
          const key = `${p.coord[0].toFixed(4)}|${p.coord[1].toFixed(4)}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        pts = filterOutliers(pts);

        if (mounted) setPositions(pts);
      } catch (err) {
        console.error("Error loading sessions for map:", err);
        if (mounted) setPositions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Update markers when positions (or map readiness) change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google || !mapReady) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (!positions.length) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    positions.forEach((p) => {
      const [lat, lng] = p.coord;
      const position = { lat, lng };

      const marker = new window.google.maps.Marker({ position, map });

      marker.addListener("click", () => {
        infoWindowRef.current.setContent(
          `<div style="min-width:160px">
             <strong>Email:</strong> ${p.meta.email || "—"}<br/>
             <strong>IP:</strong> ${p.meta.ip || "—"}<br/>
             <strong>Login:</strong> ${
               p.meta.loginTime
                 ? new Date(p.meta.loginTime).toLocaleString()
                 : "—"
             }
           </div>`
        );
        infoWindowRef.current.open(map, marker);
      });

      markersRef.current.push(marker);
      bounds.extend(position);
    });

    try {
      if (positions.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(12);
      } else {
        map.fitBounds(bounds, 60); // 60px padding, same as Leaflet version
      }
    } catch (e) {
      console.error(e);
    }
  }, [positions, mapReady]);

  // Controls
  const zoomIn = () => {
    if (mapRef.current) mapRef.current.setZoom(mapRef.current.getZoom() + 1);
  };
  const zoomOut = () => {
    if (mapRef.current) mapRef.current.setZoom(mapRef.current.getZoom() - 1);
  };
  const resetWorld = () => {
    if (mapRef.current) {
      mapRef.current.setCenter(DEFAULT_CENTER);
      mapRef.current.setZoom(DEFAULT_ZOOM);
    }
  };

  return (
    <div className="widget-card">
      <Card
        style={{
          backgroundColor: cardBg,
          border: isDark ? "none" : "1px solid #e0e0e0",
          borderRadius: "12px",
          height: "100%",
        }}
      >
        <CardBody
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            padding: "1rem",
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h5 style={{ color: textColor, margin: 0 }}>Login Sessions Map</h5>
            <div>
              <Button size="sm" outline color="primary" onClick={zoomIn} className="me-1">
                +
              </Button>
              <Button size="sm" outline color="primary" onClick={zoomOut} className="me-1">
                −
              </Button>
              <Button size="sm" outline color="secondary" onClick={resetWorld}>
                Reset
              </Button>
            </div>
          </div>

          <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", gap: 0 }}>
            <div
              ref={containerRef}
              style={{ height: "100%", width: "100%" }}
              className="chart-container map-container"
            />
          </div> 
        </CardBody>
      </Card> 
    </div>
  );
};

export default LoginAnalytics;